import React from "react";
import { Alert, Box, Divider, InputAdornment, Link, TextField, Typography } from "@mui/material";
import { Control, Controller, FieldErrors } from "react-hook-form";
import { formatCurrencyBDT } from "../../../utils/currency";
import { PaymentManagement } from "../../orders/PaymentManagement";
import { OrderFormData, PaymentDraft } from "./orderFormSchema";

const taka = (n: number) => formatCurrencyBDT(n, { minimumFractionDigits: 0 });

interface Props {
    control: Control<OrderFormData>;
    errors: FieldErrors<OrderFormData>;
    itemCount: number;
    itemsTotal: number;
    total: number;
    /** The picked delivery area, if any. */
    areaName?: string;
    /** The area's own charge, when the field holds a different amount. */
    areaChargeOffer?: number | null;
    onUseAreaCharge: () => void;
    /** "Free over ৳2,000" — when the area has a threshold. */
    freeNote?: string;
    shippingHint?: string;
    payments: PaymentDraft[];
    onPaymentsChange: (payments: PaymentDraft[]) => void;
    paymentsDisabled: boolean;
    hasBkashPayment: boolean;
}

const Row: React.FC<{ label: React.ReactNode; children: React.ReactNode }> = ({ label, children }) => (
    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
        <Typography variant="body2" color="text.secondary" component="div" sx={{ minWidth: 0 }}>
            {label}
        </Typography>
        {children}
    </Box>
);

const moneyField = { width: 120, "& input": { textAlign: "right", fontVariantNumeric: "tabular-nums" } };

/** Money in one place: items, delivery, discount, total and payments. */
export const SummaryPanel: React.FC<Props> = ({
    control,
    errors,
    itemCount,
    itemsTotal,
    total,
    areaName,
    areaChargeOffer,
    onUseAreaCharge,
    freeNote,
    shippingHint,
    payments,
    onPaymentsChange,
    paymentsDisabled,
    hasBkashPayment,
}) => (
    <Box sx={{ display: "grid", gap: 1.25 }}>
        <Typography variant="subtitle2" color="text.secondary">
            SUMMARY
        </Typography>

        <Row label={`Items (${itemCount})`}>
            <Typography variant="body2" fontWeight={600} sx={{ fontVariantNumeric: "tabular-nums" }}>
                {taka(itemsTotal)}
            </Typography>
        </Row>

        <Row
            label={
                <>
                    Delivery{areaName ? ` · ${areaName}` : ""}
                    {freeNote && (
                        <Typography variant="caption" color="text.secondary" display="block">
                            {freeNote}
                        </Typography>
                    )}
                </>
            }
        >
            <Controller
                name="shipping_amount"
                control={control}
                render={({ field }) => (
                    <TextField
                        {...field}
                        value={field.value ?? ""}
                        type="number"
                        size="small"
                        inputProps={{ min: 0, step: 1, "aria-label": "Delivery charge" }}
                        InputProps={{ startAdornment: <InputAdornment position="start">৳</InputAdornment> }}
                        error={!!errors.shipping_amount}
                        sx={moneyField}
                    />
                )}
            />
        </Row>
        {areaChargeOffer != null && (
            <Typography variant="caption" color="text.secondary" sx={{ textAlign: "right", mt: -0.75 }}>
                {areaName} charge is {taka(areaChargeOffer)} ·{" "}
                <Link component="button" type="button" variant="caption" fontWeight={600} onClick={onUseAreaCharge}>
                    Use it
                </Link>
            </Typography>
        )}
        {shippingHint && (
            <Alert severity="info" variant="outlined" sx={{ py: 0 }}>
                From chat — delivery charge: {shippingHint}
            </Alert>
        )}

        <Row label="Discount">
            <Controller
                name="discount_amount"
                control={control}
                render={({ field }) => (
                    <TextField
                        {...field}
                        value={field.value ?? ""}
                        type="number"
                        size="small"
                        inputProps={{ min: 0, step: 1, "aria-label": "Discount" }}
                        InputProps={{ startAdornment: <InputAdornment position="start">৳</InputAdornment> }}
                        error={!!errors.discount_amount}
                        sx={moneyField}
                    />
                )}
            />
        </Row>
        {total < 0 && (
            <Typography variant="caption" color="error" sx={{ textAlign: "right", mt: -0.75 }}>
                The discount is bigger than the order.
            </Typography>
        )}

        <Divider />
        <Row label={<Typography fontWeight={700}>Total</Typography>}>
            <Typography variant="h6" fontWeight={700} sx={{ fontVariantNumeric: "tabular-nums" }}>
                {taka(total)}
            </Typography>
        </Row>

        <Divider />
        <Box>
            {hasBkashPayment && (
                <Alert severity="info" sx={{ mb: 1.5 }}>
                    This order has a payment made through bKash, which can't be edited here. To refund it, use the
                    Refund action on the payment in Order Details instead — everything else on this form still saves
                    normally.
                </Alert>
            )}
            <PaymentManagement
                payments={payments}
                totalAmount={total}
                onChange={onPaymentsChange}
                disabled={paymentsDisabled}
                compact
            />
        </Box>
    </Box>
);

export default SummaryPanel;
